import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LayoutGrid, Loader2, RefreshCw, Table2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AttendanceMarkHint,
  attendanceBadgeClass,
  StudentContactSection,
} from '@/components/forms/appointment-attendance-shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermissions } from '@/hooks/usePermissions';
import { dynamicFormsApi } from '@/lib/api/dynamic-forms';
import { instructorFormsApi } from '@/lib/api/instructor-forms';
import { shortScheduleZoneFilterLabel, zonedDatetimeLocalInputToUtcIso } from '@/lib/admin-datetime';
import { DEFAULT_SCHEDULE_TIME_ZONE, formatAppointmentRangeInZone, resolveScheduleTimeZone } from '@/lib/schedule-timezone';
import { can } from '@/utils/permissions';
import type { AppointmentAttendanceListRow } from '@/types/dynamic-form';
import type { FormField } from '@/types/form-definition';
import { cn } from '@/lib/utils';

const LIST_KEY = 'join-instructor:appointment-attendance-list-view';

export default function InstructorAppointmentAttendancePage() {
  const { formId } = useParams<{ formId: string }>();
  const { permissions, adminType } = usePermissions();

  const canViewFormMeta = can(permissions, 'forms.dynamicForms.view', adminType);
  const canMarkAttendance = can(permissions, 'forms.appointments.attendance.mark', adminType);

  const [formTitle, setFormTitle] = useState<string>('');
  const [appointmentFields, setAppointmentFields] = useState<{ id: string; label: string }[]>([]);
  const [fieldScheduleZones, setFieldScheduleZones] = useState<Record<string, string>>({});

  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [statusTab, setStatusTab] = useState<'confirmed' | 'cancelled' | 'all'>('confirmed');
  const [attendanceStatus, setAttendanceStatus] = useState<'all' | 'pending' | 'attended' | 'not_attended'>('all');
  const [fieldId, setFieldId] = useState<string>('all');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [slotFromLocal, setSlotFromLocal] = useState('');
  const [slotToLocal, setSlotToLocal] = useState('');

  const [rows, setRows] = useState<AppointmentAttendanceListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingId, setMarkingId] = useState<string | null>(null);

  const [listView, setListView] = useState<'cards' | 'table'>(() => {
    try {
      const v = localStorage.getItem(LIST_KEY);
      if (v === 'table' || v === 'cards') return v;
    } catch {
      /* ignore */
    }
    return 'cards';
  });

  useEffect(() => {
    try {
      localStorage.setItem(LIST_KEY, listView);
    } catch {
      /* ignore */
    }
  }, [listView]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const slotFilterZone = useMemo(
    () =>
      fieldId !== 'all' && fieldScheduleZones[fieldId]
        ? fieldScheduleZones[fieldId]
        : DEFAULT_SCHEDULE_TIME_ZONE,
    [fieldId, fieldScheduleZones]
  );

  const derivedFieldsFromRows = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows) {
      const id = typeof r.booking.fieldId === 'string' ? r.booking.fieldId : '';
      if (!id) continue;
      m.set(id, (r.fieldLabel || id).trim());
    }
    return [...m.entries()].map(([id, label]) => ({ id, label }));
  }, [rows]);

  const fieldSelectOptions = appointmentFields.length > 0 ? appointmentFields : derivedFieldsFromRows;

  const loadFormMeta = useCallback(async () => {
    if (!formId || !canViewFormMeta) return;
    try {
      const res = await dynamicFormsApi.get(formId);
      if (res.statusCode !== 200) return;
      const f = res.data.form;
      setFormTitle(f.title);
      const fields = (f.draftDefinition?.fields ?? []) as FormField[];
      const appts = fields.filter((x) => x.type === 'appointment');
      setAppointmentFields(
        appts.map((x) => ({
          id: x.id,
          label: typeof x.label === 'string' && x.label.trim() ? x.label.trim() : x.id,
        }))
      );
      const zones: Record<string, string> = {};
      for (const x of appts) {
        const cfg = x.config && typeof x.config === 'object' && !Array.isArray(x.config) ? (x.config as Record<string, unknown>) : {};
        zones[x.id] = resolveScheduleTimeZone(typeof cfg.scheduleTimeZone === 'string' ? cfg.scheduleTimeZone : undefined);
      }
      setFieldScheduleZones(zones);
    } catch {
      /* Instructors may lack form GET; title/fields come from booking rows. */
    }
  }, [formId, canViewFormMeta]);

  const loadBookings = useCallback(async () => {
    if (!formId) return;
    setError(null);
    setLoading(true);
    try {
      const filters: Parameters<typeof instructorFormsApi.listAppointmentAttendanceBookings>[1] = {
        page,
        limit,
        statusTab,
        search: search.trim() || undefined,
        fieldId: fieldId !== 'all' ? fieldId : undefined,
        slotStartFrom: zonedDatetimeLocalInputToUtcIso(slotFromLocal, slotFilterZone),
        slotStartTo: zonedDatetimeLocalInputToUtcIso(slotToLocal, slotFilterZone),
      };
      if (statusTab === 'confirmed' && attendanceStatus !== 'all') {
        filters.attendanceStatus = attendanceStatus;
      }
      const res = await instructorFormsApi.listAppointmentAttendanceBookings(formId, filters);
      if (res.statusCode !== 200) throw new Error(res.message || 'Failed to load bookings');
      setRows(res.data.rows);
      setTotal(res.data.pagination.total);
      const t = res.data.rows[0]?.formTitle;
      if (t) setFormTitle((prev) => prev || t);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load bookings');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    formId,
    page,
    limit,
    statusTab,
    attendanceStatus,
    fieldId,
    search,
    slotFromLocal,
    slotToLocal,
    slotFilterZone,
  ]);

  useEffect(() => {
    void loadFormMeta();
  }, [loadFormMeta]);

  useEffect(() => {
    void loadBookings();
  }, [loadBookings]);

  const applySearch = () => {
    setPage(1);
    setSearch(searchDraft);
  };

  const markAttendance = async (bookingId: string, outcome: 'attended' | 'not_attended') => {
    if (!formId) return;
    setMarkingId(bookingId);
    try {
      const res = await instructorFormsApi.patchAppointmentAttendance(formId, bookingId, { outcome });
      if (res.statusCode !== 200) throw new Error(res.message || 'Could not record attendance');
      toast.success(outcome === 'attended' ? 'Marked attended' : 'Marked not attended');
      await loadBookings();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not record attendance');
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">

      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">My appointment attendance</h1>
        {formTitle ? <p className="text-muted-foreground">{formTitle}</p> : <Skeleton className="h-5 w-64" />}
        <p className="text-sm text-muted-foreground">
          Only bookings assigned to you appear here. Mark attendance after each slot starts. If a control is disabled, the server
          is enforcing session time or booking state ({DEFAULT_SCHEDULE_TIME_ZONE} used when filtering all fields).
        </p>
      </div>

      <Card className="border-border/70">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>Narrow the list, then use Refresh if needed.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <div className="grid gap-1.5">
              <Label>Booking status</Label>
              <Select
                value={statusTab}
                onValueChange={(v) => {
                  setPage(1);
                  setStatusTab(v as typeof statusTab);
                }}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {statusTab === 'confirmed' ? (
              <div className="grid gap-1.5">
                <Label>Attendance</Label>
                <Select
                  value={attendanceStatus}
                  onValueChange={(v) => {
                    setPage(1);
                    setAttendanceStatus(v as typeof attendanceStatus);
                  }}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="attended">Attended</SelectItem>
                    <SelectItem value="not_attended">Not attended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="grid gap-1.5 col-span-2 lg:col-span-1">
              <Label>Appointment field</Label>
              <Select
                value={fieldId}
                onValueChange={(v) => {
                  setPage(1);
                  setFieldId(v);
                }}
              >
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All fields (this form)</SelectItem>
                  {fieldSelectOptions.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5 col-span-2 lg:col-span-1">
              <Label htmlFor="inst-att-search">Search</Label>
              <div className="flex gap-2">
                <Input
                  id="inst-att-search"
                  placeholder="Name, email, phone…"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applySearch();
                  }}
                  className="bg-background"
                />
                <Button type="button" variant="secondary" onClick={applySearch}>
                  Apply
                </Button>
              </div>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="inst-slot-from">
                Slot start from ({shortScheduleZoneFilterLabel(slotFilterZone)})
              </Label>
              <Input
                id="inst-slot-from"
                type="datetime-local"
                value={slotFromLocal}
                onChange={(e) => {
                  setPage(1);
                  setSlotFromLocal(e.target.value);
                }}
                className="bg-background"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="inst-slot-to">
                Slot start to ({shortScheduleZoneFilterLabel(slotFilterZone)})
              </Label>
              <Input
                id="inst-slot-to"
                type="datetime-local"
                value={slotToLocal}
                onChange={(e) => {
                  setPage(1);
                  setSlotToLocal(e.target.value);
                }}
                className="bg-background"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Each bound is interpreted in {shortScheduleZoneFilterLabel(slotFilterZone)} and sent as a UTC instant. With{' '}
            <span className="font-medium">All fields</span>, bounds use {shortScheduleZoneFilterLabel(DEFAULT_SCHEDULE_TIME_ZONE)}.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => void loadBookings()}>
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/70">
        <CardHeader className="flex flex-col gap-3 space-y-0 pb-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div>
            <CardTitle className="text-base">Bookings</CardTitle>
            <CardDescription>{loading ? 'Loading…' : `${total} total`}</CardDescription>
          </div>
          <div className="grid gap-1">
            <Label className="text-muted-foreground">Layout</Label>
            <div className="flex h-10 rounded-lg border border-border/70 bg-muted/30 p-0.5">
              <Button
                type="button"
                variant={listView === 'cards' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-9 gap-1.5 px-3"
                onClick={() => setListView('cards')}
              >
                <LayoutGrid className="h-4 w-4" />
                Cards
              </Button>
              <Button
                type="button"
                variant={listView === 'table' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-9 gap-1.5 px-3"
                onClick={() => setListView('table')}
              >
                <Table2 className="h-4 w-4" />
                Table
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className={listView === 'cards' ? 'space-y-3 px-4 sm:px-5' : 'p-0 sm:p-0'}>
          {error ? (
            <div className="border-t border-border/60 px-6 py-8 text-center text-sm text-destructive">{error}</div>
          ) : loading ? (
            <div className="space-y-2 border-t border-border/60 px-6 py-8">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <div className="border-t border-border/60 px-6 py-10 text-center text-sm text-muted-foreground">
              No rows match. You may have no assigned bookings yet, or filters are too narrow.
            </div>
          ) : listView === 'cards' ? (
            <div className="space-y-3">
              {rows.map((row) => {
                const bid =
                  typeof row.booking._id === 'string' ? row.booking._id : String(row.booking._id ?? '');
                const bookingFieldId = typeof row.booking.fieldId === 'string' ? row.booking.fieldId : '';
                const slotTz = resolveScheduleTimeZone(
                  row.scheduleTimeZone,
                  row.slot?.scheduleTimeZone,
                  bookingFieldId ? fieldScheduleZones[bookingFieldId] : undefined
                );
                const studentName = row.student?.fullName?.trim() || '—';
                const studentEmail = row.student?.email?.trim() || '';
                const phone = String(row.student?.cellPhoneNumber || row.contactPhone || '').trim();
                const att = row.attendance;
                const serverEditable = att.editable === true;
                const canMark = canMarkAttendance && serverEditable;
                const slotRange = formatAppointmentRangeInZone(row.slot?.start, row.slot?.end ?? null, slotTz);
                const fieldLabel = row.fieldLabel || row.booking.fieldId || '—';

                return (
                  <Card key={bid} className="border-border/70 shadow-sm">
                    <CardContent className="space-y-4 px-4 sm:px-5">
                      <div className="flex flex-col gap-3 rounded-xl border border-primary/15 bg-primary/[0.04] px-3 py-3 sm:flex-row sm:items-stretch sm:justify-between sm:gap-4">
                        <div className="min-w-0 space-y-2">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Attendance
                          </p>
                          <Badge variant="outline" className={cn('w-fit font-normal', attendanceBadgeClass(att.code))}>
                            {att.label}
                          </Badge>
                          <AttendanceMarkHint att={att} canMark={serverEditable} />
                          {!canMarkAttendance && serverEditable ? (
                            <p className="text-xs text-muted-foreground">You don&apos;t have permission to mark attendance.</p>
                          ) : null}
                        </div>
                        {canMark && bid ? (
                          <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                            <Button
                              type="button"
                              size="default"
                              variant="default"
                              className="h-10 w-full gap-2 sm:w-auto sm:min-w-[9.5rem]"
                              disabled={markingId !== null}
                              onClick={() => void markAttendance(bid, 'attended')}
                            >
                              {markingId === bid ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                              Mark attended
                            </Button>
                            <Button
                              type="button"
                              size="default"
                              variant="outline"
                              className="h-10 w-full gap-2 sm:w-auto sm:min-w-[9.5rem]"
                              disabled={markingId !== null}
                              onClick={() => void markAttendance(bid, 'not_attended')}
                            >
                              {markingId === bid ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                              Mark not attended
                            </Button>
                          </div>
                        ) : null}
                      </div>

                      <StudentContactSection name={studentName} email={studentEmail} phone={phone} />
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-stretch">
                        <div className="rounded-lg border border-border/60 bg-muted/15 px-3 py-2.5">
                          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Slot</p>
                          <p className="mt-1.5 text-sm font-medium leading-snug text-foreground break-words">{slotRange}</p>
                          {row.slot?.label ? (
                            <p className="mt-1 text-xs text-muted-foreground break-words">{row.slot.label}</p>
                          ) : null}
                        </div>
                        <div className="rounded-lg border border-border/60 bg-muted/10 px-3 py-2.5">
                          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                            Appointment field
                          </p>
                          <p className="mt-1.5 text-sm font-medium leading-snug text-foreground break-words">{fieldLabel}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-b-xl border-t border-border/60">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="align-top whitespace-normal">Student</TableHead>
                    <TableHead className="align-top whitespace-normal">Slot</TableHead>
                    <TableHead className="align-top whitespace-normal">Field</TableHead>
                    <TableHead className="min-w-[200px] align-top whitespace-normal">Attendance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const bid =
                      typeof row.booking._id === 'string'
                        ? row.booking._id
                        : String(row.booking._id ?? '');
                    const bookingFieldId = typeof row.booking.fieldId === 'string' ? row.booking.fieldId : '';
                    const slotTz = resolveScheduleTimeZone(
                      row.scheduleTimeZone,
                      row.slot?.scheduleTimeZone,
                      bookingFieldId ? fieldScheduleZones[bookingFieldId] : undefined
                    );
                    const studentName = row.student?.fullName?.trim() || '—';
                    const studentEmail = row.student?.email?.trim() || '';
                    const phone = String(row.student?.cellPhoneNumber || row.contactPhone || '').trim();
                    const att = row.attendance;
                    const serverEditable = att.editable === true;
                    const canMark = canMarkAttendance && serverEditable;

                    return (
                      <TableRow key={bid}>
                        <TableCell className="max-w-[min(260px,36vw)] align-top whitespace-normal text-sm">
                          <StudentContactSection
                            name={studentName}
                            email={studentEmail}
                            phone={phone}
                            compact
                          />
                        </TableCell>
                        <TableCell className="max-w-[min(280px,36vw)] align-top whitespace-normal text-sm leading-snug">
                          {formatAppointmentRangeInZone(row.slot?.start, row.slot?.end ?? null, slotTz)}
                          {row.slot?.label ? (
                            <div className="mt-1 break-words text-xs text-muted-foreground">{row.slot.label}</div>
                          ) : null}
                        </TableCell>
                        <TableCell className="max-w-[min(260px,34vw)] align-top whitespace-normal text-sm leading-snug break-words">
                          {row.fieldLabel || row.booking.fieldId || '—'}
                        </TableCell>
                        <TableCell className="align-top whitespace-normal">
                          <div className="flex max-w-[min(320px,42vw)] flex-col gap-2">
                            <Badge variant="outline" className={cn('w-fit font-normal', attendanceBadgeClass(att.code))}>
                              {att.label}
                            </Badge>
                            <AttendanceMarkHint att={att} canMark={serverEditable} />
                            {!canMarkAttendance && serverEditable ? (
                              <p className="text-xs text-muted-foreground">No mark permission.</p>
                            ) : null}
                            {canMark && bid ? (
                              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="default"
                                  className="h-9 w-full gap-1.5 sm:w-auto sm:min-w-[8.5rem]"
                                  disabled={markingId !== null}
                                  onClick={() => void markAttendance(bid, 'attended')}
                                >
                                  {markingId === bid ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                                  Mark attended
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-9 w-full gap-1.5 sm:w-auto sm:min-w-[8.5rem]"
                                  disabled={markingId !== null}
                                  onClick={() => void markAttendance(bid, 'not_attended')}
                                >
                                  {markingId === bid ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                                  Mark not attended
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {!loading && !error ? (
            <div
              className={cn(
                'flex flex-wrap items-center justify-end gap-2 border-t border-border/60 py-3',
                listView === 'cards' ? 'px-4 sm:px-5' : 'px-4 sm:px-6'
              )}
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
